import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Users, RefreshCw, UserRound, Mail, Phone } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async (search = query) => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/customers', { params: { search, limit: 200 } });
      setCustomers(data.customers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(''); }, []);

  const submit = (e) => {
    e?.preventDefault();
    load(query.trim());
  };

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow"><Users size={13} /> Customer operations</div>
          <h1>Customers</h1>
          <p>Find customer accounts quickly without exposing credentials or sensitive authentication fields.</p>
        </div>
        <button className="admin-lux-button secondary" onClick={() => load()} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <form className="admin-card" onSubmit={submit}>
        <div className="admin-card-head">
          <div><h2>Search customers</h2><p>Name, phone or email. Search is handled server-side.</p></div>
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="pl-9"
              placeholder="Search by name, phone or email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="admin-lux-button" type="submit">Search</button>
        </div>
      </form>

      <div className="admin-card mt-4">
        <div className="admin-card-head">
          <div><h2>Customer directory</h2><p>{customers.length} visible</p></div>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Customer</th><th>Contact</th><th>City</th><th>Joined</th></tr></thead>
            <tbody>
              {customers.map((customer, index) => (
                <motion.tr key={customer.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.015 }}>
                  <td>
                    <div className="admin-person-cell">
                      <div className="admin-table-avatar"><UserRound size={15} /></div>
                      <div><strong>{customer.name || 'Unnamed customer'}</strong><span>{customer.id}</span></div>
                    </div>
                  </td>
                  <td>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1"><Phone size={11} /> {customer.phone || '—'}</div>
                      <div className="flex items-center gap-1"><Mail size={11} /> {customer.email || '—'}</div>
                    </div>
                  </td>
                  <td>{customer.city || '—'}</td>
                  <td>{customer.created_at ? new Date(customer.created_at).toLocaleString() : '—'}</td>
                </motion.tr>
              ))}
              {!loading && customers.length === 0 && (
                <tr><td colSpan="4"><div className="admin-empty">No customers found.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
